/**
 * Theme helper — persists the user's light/dark choice and applies it to
 * <html data-theme>. When no explicit choice is stored we leave the
 * attribute off so the CSS `@media (prefers-color-scheme)` block follows
 * the OS. See styles/theme.css for the token resolution order.
 */

const THEME_KEY = 'omni:theme';

/** @typedef {'light' | 'dark'} Theme */

/** Matches the title bar / sidebar gradient in each mode (index.html's
 *  static <meta theme-color> is just the dark default for first paint). */
const THEME_COLOR = { light: '#f8fafc', dark: '#1e293b' };

/** Keeps the mobile browser chrome / PWA status bar colour in step with
 *  the app's actual light/dark state instead of the static page default. */
function syncMetaThemeColor(theme) {
  try {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', THEME_COLOR[theme] ?? THEME_COLOR.dark);
  } catch {
    /* ignore (no document, e.g. SSR/tests) */
  }
}

/** @returns {Theme | null} the stored explicit choice, or null to follow the OS */
export function readStoredTheme() {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

/** @returns {boolean} whether the OS currently prefers dark */
export function osPrefersDark() {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

/**
 * The theme actually in effect right now (stored choice, else OS).
 * @returns {Theme}
 */
export function effectiveTheme() {
  return readStoredTheme() ?? (osPrefersDark() ? 'dark' : 'light');
}

/**
 * Apply a theme. `null` clears the explicit choice → follow the OS.
 * @param {Theme | null} theme
 */
export function applyTheme(theme) {
  const root = document.documentElement;
  try {
    if (theme === 'light' || theme === 'dark') {
      root.setAttribute('data-theme', theme);
      localStorage.setItem(THEME_KEY, theme);
    } else {
      root.removeAttribute('data-theme');
      localStorage.removeItem(THEME_KEY);
    }
  } catch {
    if (theme) root.setAttribute('data-theme', theme);
  }
  syncMetaThemeColor(theme ?? (osPrefersDark() ? 'dark' : 'light'));
}

/**
 * Run once before first render (from main.jsx) so there's no light flash
 * on a dark-preferring session. Only stamps the attribute when the user
 * has an explicit stored choice; otherwise the media query drives it.
 */
export function initTheme() {
  const stored = readStoredTheme();
  if (stored) document.documentElement.setAttribute('data-theme', stored);
  syncMetaThemeColor(stored ?? (osPrefersDark() ? 'dark' : 'light'));
}
